/**
 * ReadinessTab.jsx — Phase G.5 Workforce Readiness tab.
 * Purpose: operational snapshot for supervisors — availability, skills,
 * certifications, assigned devices, and manager-only operational notes.
 *
 * Locked design decisions:
 *   §14 Rule 2 — Operational notes hidden from the subject themselves.
 *   §15 — Six operational statuses (available/on_duty/busy/on_break/off_duty/training).
 *   D11 — Employees may view own skills/certs/devices/status; managers write.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Activity, Award, Cpu, StickyNote, Plus, Trash2, RefreshCw,
  Radio, Smartphone, Tablet, CreditCard, MonitorSmartphone, HelpCircle,
  AlertTriangle, CheckCircle2, Circle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/primitives";
import { DatePicker } from "@/components/ui/date-picker";

export const AVAILABILITY_STATES = [
  { code: "available", label: "Available", tone: "success",
    hint: "On duty and immediately available for work assignment." },
  { code: "on_duty",   label: "On Duty",   tone: "info",
    hint: "Shift started but may be in briefing, setup, or admin work." },
  { code: "busy",      label: "Busy",      tone: "warning",
    hint: "Currently performing an assigned operational task." },
  { code: "on_break",  label: "On Break",  tone: "warning",
    hint: "Temporarily unavailable." },
  { code: "off_duty",  label: "Off Duty",  tone: "neutral",
    hint: "Not working." },
  { code: "training",  label: "Training",  tone: "info",
    hint: "Unavailable due to training." },
];

const DEVICE_TYPES = [
  { code: "mobile",        label: "Mobile",        icon: Smartphone },
  { code: "tablet",        label: "Tablet",        icon: Tablet },
  { code: "pos_terminal",  label: "POS Terminal",  icon: MonitorSmartphone },
  { code: "access_card",   label: "Access Card",   icon: CreditCard },
  { code: "radio",         label: "Radio",         icon: Radio },
  { code: "other",         label: "Other",         icon: HelpCircle },
];
const deviceIcon = (t) => (DEVICE_TYPES.find(d => d.code === t)?.icon) || HelpCircle;

function statusOf(code) { return AVAILABILITY_STATES.find(s => s.code === code) || AVAILABILITY_STATES[4]; }

function SectionCard({ title, icon: Icon, action, children, testid }) {
  return (
    <section className="hocp-card p-5" data-testid={testid}>
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-hocp-textSecondary" strokeWidth={1.75}/>
          <h3 className="font-display font-semibold text-[15px] text-hocp-textPrimary">{title}</h3>
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════ */
export default function ReadinessTab({ tm, isSuperAdmin, canManage, canViewNotes, isSelfView }) {
  const [card, setCard]   = useState(null);
  const [skillCat, setSkillCat] = useState([]);
  const [certCat,  setCertCat]  = useState([]);
  const [notes,    setNotes]    = useState([]);
  const [loading,  setLoading]  = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: s }, { data: cx }] = await Promise.all([
        api.get(`/team-members/${tm.id}/readiness-card`),
        api.get(`/readiness/skills/catalog`),
        api.get(`/readiness/certifications/catalog`),
      ]);
      setCard(c);
      setSkillCat(s);
      setCertCat(cx);
      if (canViewNotes && !isSelfView) {
        try {
          const { data: n } = await api.get(`/team-members/${tm.id}/operational-notes`);
          setNotes(n);
        } catch { setNotes([]); }
      }
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load readiness data");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tm.id]);

  if (loading || !card) {
    return <div className="p-6 text-hocp-textSecondary text-sm" data-testid="readiness-loading">Loading readiness…</div>;
  }

  return (
    <div className="space-y-4" data-testid="readiness-tab">
      <AvailabilityCard card={card} canManage={canManage || isSelfView} tmId={tm.id} onChanged={load}/>
      <SkillsCard card={card} catalog={skillCat} canManage={canManage} tmId={tm.id} onChanged={load}/>
      <CertificationsCard card={card} catalog={certCat} canManage={canManage} tmId={tm.id} onChanged={load}/>
      <DevicesCard card={card} canManage={canManage} tmId={tm.id} onChanged={load}/>
      {canViewNotes && !isSelfView && (
        <NotesCard notes={notes} canManage={canManage} tmId={tm.id} onChanged={load}/>
      )}
      <MetaFooter card={card}/>
    </div>
  );
}

/* ── Availability ────────────────────────────────────────────────── */
function AvailabilityCard({ card, canManage, tmId, onChanged }) {
  const [saving, setSaving] = useState(false);
  const cur = statusOf(card.availability?.status);
  const setStatus = async (code, note = card.availability?.status_note) => {
    setSaving(true);
    try {
      await api.put(`/team-members/${tmId}/availability`, {
        status: code, status_note: note || null,
      });
      toast.success(`Availability set to ${statusOf(code).label}`);
      await onChanged();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <SectionCard title="Operational Status" icon={Activity} testid="readiness-availability"
      action={<Badge tone={cur.tone} size="md" data-testid="readiness-current-status">{cur.label}</Badge>}>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
        {AVAILABILITY_STATES.map(s => (
          <button key={s.code} disabled={!canManage || saving}
            title={s.hint}
            onClick={() => setStatus(s.code)}
            data-testid={`readiness-status-${s.code}`}
            className={`text-left px-3 py-2 rounded-lg border transition
              ${cur.code === s.code
                ? "border-hocp-navy bg-hocp-navy/5 text-hocp-navy shadow-sm"
                : "border-hocp-border hover:border-hocp-navy/40 hover:bg-hocp-alice/40"}
              ${!canManage ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
            <div className="text-[13px] font-semibold">{s.label}</div>
            <div className="text-[11px] text-hocp-textTertiary leading-tight mt-0.5">{s.hint}</div>
          </button>
        ))}
      </div>
      {card.availability?.status_note && (
        <div className="mt-3 text-[13px] text-hocp-textSecondary italic" data-testid="readiness-status-note">
          Note: {card.availability.status_note}
        </div>
      )}
      {canManage && (
        <div className="mt-3 flex gap-2">
          <Input placeholder="Optional status note (e.g. Back 14:35)" maxLength={60}
                 data-testid="readiness-status-note-input" aria-label="Status note"
                 defaultValue={card.availability?.status_note || ""}
                 onKeyDown={(e) => { if (e.key === "Enter") setStatus(card.availability?.status, e.target.value); }}/>
          <Button size="sm" onClick={(e) => setStatus(card.availability?.status, e.currentTarget.parentElement.querySelector("input").value)}
                  data-testid="readiness-save-note">Save note</Button>
        </div>
      )}
      <div className="mt-2 text-[11px] text-hocp-textTertiary">
        Last changed {(card.availability?.changed_at || "").slice(0, 19).replace("T", " ")}
      </div>
    </SectionCard>
  );
}

/* ── Skills ──────────────────────────────────────────────────────── */
function SkillsCard({ card, catalog, canManage, tmId, onChanged }) {
  const [picking, setPicking] = useState(false);
  const [pick, setPick] = useState("");
  const owned = new Set((card.skills || []).map(s => s.code));
  const available = catalog.filter(c => !owned.has(c.code));

  const grant = async () => {
    if (!pick) return;
    try {
      await api.post(`/team-members/${tmId}/skills`, { skill_code: pick });
      toast.success("Skill added"); setPick(""); setPicking(false); await onChanged();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };
  const revoke = async (code) => {
    if (!window.confirm(`Remove skill "${code}"?`)) return;
    try {
      await api.delete(`/team-members/${tmId}/skills/${code}`);
      toast.success("Skill removed"); await onChanged();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };
  return (
    <SectionCard title="Skills" icon={CheckCircle2} testid="readiness-skills"
      action={canManage && !picking && (
        <Button size="sm" variant="outline" onClick={() => setPicking(true)} data-testid="readiness-skill-add">
          <Plus className="w-3.5 h-3.5 mr-1"/> Add skill
        </Button>
      )}>
      {picking && (
        <div className="flex gap-2 mb-3" data-testid="readiness-skill-picker">
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger className="h-9" data-testid="readiness-skill-select" aria-label="Choose skill">
              <SelectValue placeholder="Choose skill..."/>
            </SelectTrigger>
            <SelectContent>
              {available.map(s => (
                <SelectItem key={s.code} value={s.code}>{s.label} <span className="opacity-50 ml-2 text-[11px]">{s.category}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={grant} data-testid="readiness-skill-confirm">Add</Button>
          <Button size="sm" variant="outline" onClick={() => { setPicking(false); setPick(""); }}>Cancel</Button>
        </div>
      )}
      {(!card.skills || card.skills.length === 0) ? (
        <p className="text-[13px] text-hocp-textTertiary italic">No skills assigned.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {card.skills.map(s => (
            <span key={s.code} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-hocp-alice/60 border border-hocp-border text-[12.5px]"
                  data-testid={`readiness-skill-chip-${s.code}`}>
              <span className="font-medium text-hocp-textPrimary">{s.label}</span>
              <span className="text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">{s.category?.replace(/_/g, " ")}</span>
              {canManage && (
                <button onClick={() => revoke(s.code)} className="text-hocp-textTertiary hover:text-hocp-error ml-1"
                        data-testid={`readiness-skill-remove-${s.code}`}>
                  <Trash2 className="w-3.5 h-3.5"/>
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

/* ── Certifications ──────────────────────────────────────────────── */
function CertificationsCard({ card, catalog, canManage, tmId, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ certification_code: "", issued_on: "", expires_on: "", issuer: "" });

  const submit = async () => {
    if (!form.certification_code || !form.issued_on) return toast.error("Certification and issued date required");
    try {
      await api.post(`/team-members/${tmId}/certifications`, {
        certification_code: form.certification_code,
        issued_on: form.issued_on,
        expires_on: form.expires_on || null,
        issuer: form.issuer || null,
      });
      toast.success("Certification issued");
      setAdding(false); setForm({ certification_code: "", issued_on: "", expires_on: "", issuer: "" });
      await onChanged();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };
  const archive = async (id) => {
    if (!window.confirm("Archive this certification?")) return;
    try { await api.delete(`/team-members/${tmId}/certifications/${id}`); toast.success("Archived"); await onChanged(); }
    catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };

  const toneFor = (c) => c.is_expired ? "error" : c.is_expiring_soon ? "warning" : "success";
  const iconFor = (c) => c.is_expired ? AlertTriangle : c.is_expiring_soon ? AlertTriangle : CheckCircle2;

  return (
    <SectionCard title="Certifications" icon={Award} testid="readiness-certifications"
      action={canManage && !adding && (
        <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="readiness-cert-add">
          <Plus className="w-3.5 h-3.5 mr-1"/> Issue certification
        </Button>
      )}>
      {adding && (
        <div className="grid grid-cols-2 gap-2 mb-3 p-3 rounded-lg bg-hocp-alice/30" data-testid="readiness-cert-form">
          <Select value={form.certification_code} onValueChange={(v) => setForm(f => ({ ...f, certification_code: v }))}>
            <SelectTrigger data-testid="readiness-cert-select" aria-label="Certification"><SelectValue placeholder="Certification..."/></SelectTrigger>
            <SelectContent>
              {catalog.map(c => <SelectItem key={c.code} value={c.code}>{c.label}{c.regulatory && " (regulatory)"}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Issuer (optional)" value={form.issuer} onChange={(e) => setForm(f => ({ ...f, issuer: e.target.value }))}
                 data-testid="readiness-cert-issuer" aria-label="Issuer"/>
          <div>
            <label className="text-[11px] text-hocp-textTertiary">Issued on</label>
            <DatePicker value={(form.issued_on || '').slice(0,10)} onChange={(_v) => setForm(f => ({ ...f, issued_on: _v }))} data-testid="readiness-cert-issued"/>
          </div>
          <div>
            <label className="text-[11px] text-hocp-textTertiary">Expires on (auto if blank)</label>
            <DatePicker value={(form.expires_on || '').slice(0,10)} onChange={(_v) => setForm(f => ({ ...f, expires_on: _v }))} data-testid="readiness-cert-expires"/>
          </div>
          <div className="col-span-2 flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
            <Button size="sm" onClick={submit} data-testid="readiness-cert-confirm">Issue</Button>
          </div>
        </div>
      )}
      {(!card.certifications || card.certifications.length === 0) ? (
        <p className="text-[13px] text-hocp-textTertiary italic">No certifications on file.</p>
      ) : (
        <div className="space-y-2">
          {card.certifications.map(c => {
            const Icon = iconFor(c);
            return (
              <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border border-hocp-border"
                   data-testid={`readiness-cert-row-${c.code}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <Icon className={`w-4 h-4 shrink-0 ${c.is_expired ? "text-hocp-error" : c.is_expiring_soon ? "text-yellow-600" : "text-emerald-600"}`}/>
                  <div className="min-w-0">
                    <div className="text-[14px] font-medium text-hocp-textPrimary truncate">
                      {c.label} {c.regulatory && <span className="text-[10px] uppercase text-hocp-textTertiary ml-1">Regulatory</span>}
                    </div>
                    <div className="text-[11.5px] text-hocp-textSecondary">
                      Issued {c.issued_on} · {c.expires_on ? `Expires ${c.expires_on}` : "No expiry"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge tone={toneFor(c)} size="sm">{c.expiry_status.replace("_", " ")}</Badge>
                  {canManage && (
                    <button onClick={() => archive(c.id)} className="text-hocp-textTertiary hover:text-hocp-error"
                            data-testid={`readiness-cert-archive-${c.code}`}>
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

/* ── Devices ─────────────────────────────────────────────────────── */
function DevicesCard({ card, canManage, tmId, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ device_name: "", device_type: "radio", serial_number: "", assigned_date: "" });

  const assign = async () => {
    if (!form.device_name || !form.assigned_date) return toast.error("Device name and assigned date required");
    try {
      await api.post(`/team-members/${tmId}/devices`, {
        device_name: form.device_name, device_type: form.device_type,
        serial_number: form.serial_number || null, assigned_date: form.assigned_date,
      });
      toast.success("Device assigned");
      setAdding(false); setForm({ device_name: "", device_type: "radio", serial_number: "", assigned_date: "" });
      await onChanged();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };
  const changeStatus = async (dev_id, status) => {
    const notes = status === "returned" ? null : window.prompt(`Notes for ${status}:`) || null;
    const returned_date = status === "returned" ? new Date().toISOString().slice(0, 10) : null;
    try {
      await api.patch(`/team-members/${tmId}/devices/${dev_id}`, { status, returned_date, notes });
      toast.success(`Device marked ${status}`); await onChanged();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };

  return (
    <SectionCard title="Assigned Devices" icon={Cpu} testid="readiness-devices"
      action={canManage && !adding && (
        <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="readiness-device-add">
          <Plus className="w-3.5 h-3.5 mr-1"/> Assign device
        </Button>
      )}>
      {adding && (
        <div className="grid grid-cols-2 gap-2 mb-3 p-3 rounded-lg bg-hocp-alice/30" data-testid="readiness-device-form">
          <Input placeholder="Device name (e.g. Radio-7)" value={form.device_name}
                 onChange={(e) => setForm(f => ({ ...f, device_name: e.target.value }))}
                 data-testid="readiness-device-name" aria-label="Device name"/>
          <Select value={form.device_type} onValueChange={(v) => setForm(f => ({ ...f, device_type: v }))}>
            <SelectTrigger data-testid="readiness-device-type" aria-label="Device type"><SelectValue/></SelectTrigger>
            <SelectContent>
              {DEVICE_TYPES.map(t => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Serial number (optional)" value={form.serial_number}
                 onChange={(e) => setForm(f => ({ ...f, serial_number: e.target.value }))}
                 data-testid="readiness-device-serial" aria-label="Serial number"/>
          <DatePicker value={(form.assigned_date || '').slice(0,10)} onChange={(_v) => setForm(f => ({ ...f, assigned_date: _v }))} data-testid="readiness-device-date"/>
          <div className="col-span-2 flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
            <Button size="sm" onClick={assign} data-testid="readiness-device-confirm">Assign</Button>
          </div>
        </div>
      )}
      {(!card.devices || card.devices.length === 0) ? (
        <p className="text-[13px] text-hocp-textTertiary italic">No devices assigned.</p>
      ) : (
        <div className="space-y-2">
          {card.devices.map(d => {
            const Icon = deviceIcon(d.device_type);
            return (
              <div key={d.id} className="flex items-center justify-between p-3 rounded-lg border border-hocp-border"
                   data-testid={`readiness-device-row-${d.id}`}>
                <div className="flex items-center gap-3">
                  <Icon className="w-4 h-4 text-hocp-textSecondary"/>
                  <div>
                    <div className="text-[14px] font-medium text-hocp-textPrimary">{d.device_name}</div>
                    <div className="text-[11.5px] text-hocp-textSecondary">
                      {d.device_type.replace("_", " ")} {d.serial_number && `· SN ${d.serial_number}`} · Assigned {d.assigned_date}
                    </div>
                  </div>
                </div>
                {canManage && (
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => changeStatus(d.id, "returned")}
                            data-testid={`readiness-device-return-${d.id}`}>Return</Button>
                    <Button size="sm" variant="outline" onClick={() => changeStatus(d.id, "lost")}>Lost</Button>
                    <Button size="sm" variant="outline" onClick={() => changeStatus(d.id, "damaged")}>Damaged</Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

/* ── Ops Notes (managers only) ──────────────────────────────────── */
function NotesCard({ notes, canManage, tmId, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const add = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/team-members/${tmId}/operational-notes`, { note: text.trim() });
      toast.success("Note added"); setAdding(false); setText(""); await onChanged();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };
  const archive = async (id) => {
    if (!window.confirm("Archive this note?")) return;
    try { await api.delete(`/team-members/${tmId}/operational-notes/${id}`); toast.success("Archived"); await onChanged(); }
    catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };
  return (
    <SectionCard title="Operational Notes (Managers Only)" icon={StickyNote} testid="readiness-notes"
      action={canManage && !adding && (
        <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="readiness-note-add">
          <Plus className="w-3.5 h-3.5 mr-1"/> Add note
        </Button>
      )}>
      <div className="text-[11px] text-hocp-textTertiary mb-2 italic">
        These notes are visible to managers only. They are never shown to the team member.
      </div>
      {adding && (
        <div className="mb-3 space-y-2">
          <textarea rows={3} className="w-full rounded-lg border border-hocp-border p-2 text-[13px] focus:outline-none focus:border-hocp-navy"
            placeholder="e.g. Speaks Tamil and Hindi. Night-shift capable."
            maxLength={500} value={text} onChange={(e) => setText(e.target.value)}
            data-testid="readiness-note-textarea" aria-label="Manager note"/>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
            <Button size="sm" onClick={add} data-testid="readiness-note-confirm">Save</Button>
          </div>
        </div>
      )}
      {notes.length === 0 ? (
        <p className="text-[13px] text-hocp-textTertiary italic">No operational notes.</p>
      ) : (
        <div className="space-y-2">
          {notes.map(n => (
            <div key={n.id} className="p-3 rounded-lg border border-hocp-border bg-white"
                 data-testid={`readiness-note-${n.id}`}>
              <p className="text-[13.5px] text-hocp-textPrimary whitespace-pre-wrap">{n.note}</p>
              <div className="flex items-center justify-between mt-1.5 text-[11px] text-hocp-textTertiary">
                <span>By {n.created_by_email || n.created_by} · {n.created_at?.slice(0, 19).replace("T", " ")}</span>
                {canManage && (
                  <button onClick={() => archive(n.id)} className="hover:text-hocp-error"
                          data-testid={`readiness-note-archive-${n.id}`}>
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

/* ── Meta footer ─────────────────────────────────────────────────── */
function MetaFooter({ card }) {
  return (
    <div className="text-[11px] text-hocp-textTertiary text-right pt-2" data-testid="readiness-last-updated">
      Card v{card.readiness_card_version} · Last updated {(card.last_updated || "").slice(0, 19).replace("T", " ")}
    </div>
  );
}
